/**
 * Notifications Utility
 * Handles browser notifications and employee reminder system
 */

/**
 * Request permission for browser notifications
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications')
    return false
  }

  if (Notification.permission === 'granted') {
    return true
  }

  if (Notification.permission !== 'denied') {
    try {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }

  return false
}

/**
 * Send a desktop notification
 */
export const sendDesktopNotification = (title, options = {}) => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported')
    return
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted')
    return
  }

  try {
    const notification = new Notification(title, {
      icon: '/meeting-icon.png',
      badge: '/meeting-badge.png',
      tag: 'meeting-notification',
      requireInteraction: false,
      ...options,
    })

    // Auto close after 10 seconds if not interacted with
    const timeout = setTimeout(() => {
      notification.close()
    }, 10000)

    notification.onclick = () => {
      clearTimeout(timeout)
      notification.close()
      window.focus()
    }

    notification.onclose = () => {
      clearTimeout(timeout)
    }

    return notification
  } catch (error) {
    console.error('Error sending notification:', error)
  }
}

/**
 * Send email notification (mock - would integrate with backend)
 */
export const sendEmailNotification = async (attendeeEmail, meetingInfo, type = 'reminder') => {
  try {
    // This would connect to a backend email service
    console.log(`Email notification sent to ${attendeeEmail}:`, {
      type,
      meeting: meetingInfo.title,
      time: meetingInfo.startTime,
    })

    // Mock API call
    const response = await fetch('/api/notifications/email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: attendeeEmail,
        type,
        meetingId: meetingInfo.id,
        meetingTitle: meetingInfo.title,
        startTime: meetingInfo.startTime,
        conferenceLink: meetingInfo.conferenceLink,
      }),
    }).catch(() => {
      // Mock response if no backend
      return { ok: true }
    })

    return response
  } catch (error) {
    console.error('Error sending email notification:', error)
  }
}

/**
 * Calculate time until meeting
 */
export const getTimeUntilMeeting = (meetingStartTime) => {
  const now = new Date()
  const start = new Date(meetingStartTime)
  const diffMs = start - now
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)

  if (diffMins < 0) {
    return null
  }

  return {
    ms: diffMs,
    minutes: diffMins,
    hours: diffHours,
    display: diffHours > 0 ? `${diffHours}h ${diffMins % 60}m` : `${diffMins}m`,
  }
}

/**
 * Format meeting info for notification
 */
export const formatMeetingForNotification = (meeting) => {
  const timeUntil = getTimeUntilMeeting(meeting.startTime)

  return {
    title: `Meeting in ${timeUntil?.display || 'soon'}`,
    body: `${meeting.title} with ${meeting.attendees.length} attendees`,
    tag: `meeting-${meeting.id}`,
    requireInteraction: timeUntil?.minutes <= 5,
  }
}

/**
 * Store notification preferences in localStorage
 */
export const saveNotificationPreferences = (preferences) => {
  try {
    localStorage.setItem('notification-preferences', JSON.stringify(preferences))
  } catch (error) {
    console.error('Error saving notification preferences:', error)
  }
}

/**
 * Get notification preferences from localStorage
 */
export const getNotificationPreferences = () => {
  try {
    const preferences = localStorage.getItem('notification-preferences')
    return preferences ? JSON.parse(preferences) : {
      desktopNotifications: true,
      emailNotifications: true,
      reminderMinutes: [15, 5], // Remind 15 min and 5 min before
      soundEnabled: true,
    }
  } catch (error) {
    console.error('Error getting notification preferences:', error)
    return {
      desktopNotifications: true,
      emailNotifications: true,
      reminderMinutes: [15, 5],
      soundEnabled: true,
    }
  }
}

/**
 * Play notification sound
 */
export const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    // Create a pleasant bell-like sound
    oscillator.frequency.value = 800
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.5)
  } catch (error) {
    console.error('Error playing notification sound:', error)
  }
}

/**
 * Check if attendee should be notified
 */
export const shouldNotifyAttendee = (attendee, notificationType) => {
  if (!attendee.notificationPreference) {
    return false
  }

  if (notificationType === 'desktop') {
    return attendee.notificationPreference === 'desktop' || attendee.notificationPreference === 'both'
  }

  if (notificationType === 'email') {
    return attendee.notificationPreference === 'email' || attendee.notificationPreference === 'both'
  }

  return false
}
